import * as React from 'react';

import {Avatar} from 'shells/avatar/avatar';
// @ts-expect-error - imported from .js/.jsx file
import {UserGroupIcon} from 'shells/icon/svg/user-group-icon';
import {SelectPickerGenericMulti} from 'shells/select-picker-generic/select-picker-generic-multi';
import {TwoLine} from 'shells/two-line/two-line';

import type {MeetingType} from 'nutshell-graphql-types';

import {COHOSTED} from './create-meeting-modal';
import {HostTypeSelectRadio} from './host-type-select-radio';
import type {CohostUserOption} from './use-get-cohost-user-options';

import styles from './meeting-host-type-selection.module.css';

type Props = {
    cohostUserOptions: CohostUserOption[] | null | undefined;
    selectedCohostIds: string[];
    setSelectedCohostIds: (userIds: string[]) => void;
    setSelectedTeamId?: (teamId?: string | null | undefined) => void;
    selectedHostType?: MeetingType;
    setSelectedHostType?: (hostType: MeetingType) => void;
    isDuplicateMeeting?: boolean;
    onSearchChange?: (search: string) => void;
};

export function MeetingCohostSelect(props: Props) {
    const {cohostUserOptions, selectedCohostIds, onSearchChange} = props;

    const selectContainerRef = React.useRef(null);

    // @ts-expect-error
    const handleInputChange = (inputValue: string, {action}) => {
        // Only update search on user input, not on other actions like menu-close
        if (action === 'input-change' && onSearchChange) {
            onSearchChange(inputValue);
        }
    };

    const handleMenuClose = () => {
        if (onSearchChange) {
            onSearchChange('');
        }
    };

    const selectedCohostOptions =
        cohostUserOptions && selectedCohostIds
            ? cohostUserOptions.filter((option) => selectedCohostIds.includes(option.value))
            : [];

    const cohostSelectPicker = (
        <SelectPickerGenericMulti
            name='co-host-select'
            placeholder='Select co-host(s)…'
            tokenizedStyle='users'
            value={selectedCohostOptions}
            isMulti={true}
            closeMenuOnSelect={false}
            isLoading={!cohostUserOptions}
            // @ts-expect-error
            onChange={(selectedOptions) => {
                // @ts-expect-error
                props.setSelectedCohostIds(selectedOptions.map((option) => option.value));
            }}
            onClear={() => {
                props.setSelectedCohostIds([]);
            }}
            onInputChange={onSearchChange ? handleInputChange : undefined}
            onMenuClose={onSearchChange ? handleMenuClose : undefined}
            menuPortalTarget={
                // Portals the select menu within the container so that it can overhang outside the modal
                // @ts-expect-error
                selectContainerRef.current instanceof HTMLElement
                    ? selectContainerRef.current
                    : undefined
            }
            // @ts-expect-error
            formatOptionLabel={(option) => (
                <TwoLine
                    key={option.value}
                    topRow={option.label}
                    bottomRow={option.isDisabled && 'No calendar sync enabled'}
                    avatar={
                        <Avatar
                            type='users'
                            size={24}
                            avatarUrl={option.avatarUrl}
                            initials={option.initials}
                        />
                    }
                    shouldCenterSingleLine={true}
                    isLargeWithSmallBottomRow={true}
                    isDisabled={option.isDisabled}
                />
            )}
            options={cohostUserOptions || []}
        />
    );

    // This component is used in two places: the create meeting modal and the add cohosts modal. The ternary will show just the picker itself in the latter case and include the radio button in the former.
    return props.isDuplicateMeeting ? (
        <div ref={selectContainerRef}>{cohostSelectPicker}</div>
    ) : (
        <div ref={selectContainerRef}>
            <HostTypeSelectRadio
                value={COHOSTED}
                label='Co-host'
                sublabel='Only the times that work for all hosts will be shown to invitees.'
                icon={<UserGroupIcon size={13} wrapWithDiv={false} />}
            />
            <div
                onClick={() => {
                    if (props.selectedHostType !== COHOSTED) {
                        props.setSelectedHostType?.(COHOSTED);
                        props.setSelectedTeamId?.(null);
                    }
                }}
                className={
                    props.selectedHostType !== COHOSTED
                        ? styles['select-container--disabled']
                        : styles['select-container']
                }
            >
                {/* Hide when other options are selected EXCEPT always show for non-feature flagged users since they can't see round robin yet */}
                {props.selectedHostType === COHOSTED && cohostSelectPicker}
            </div>
        </div>
    );
}
